/**
 * WebSocket Keepalive Utility
 * Handles ping/pong mechanism to keep WebSocket connections alive
 * Backend sends pings, frontend responds with pongs
 */

class WebSocketKeepalive {
    constructor(websocket, options = {}) {
        this.websocket = websocket;
        this.options = {
            autoReconnect: options.autoReconnect !== false,
            reconnectInterval: options.reconnectInterval || 10000, // 10 seconds - less aggressive
            maxReconnectAttempts: options.maxReconnectAttempts || 3, // Fewer attempts
            debug: options.debug || false,
            // Aligned with backend PING_TIMEOUT (90s) - fast dead-connection detection
            // Visibility handler resets timer when tab returns from background to prevent false disconnections
            pingTimeout: options.pingTimeout || 90000, // 90 seconds - aligned with backend WS_SETTINGS.PING_TIMEOUT
            managedMode: options.managedMode || false, // When true, don't handle ping/pong
            ...options
        };
        
        this.reconnectAttempts = 0;
        this.originalUrl = null;
        this.originalProtocols = null;
        this.onReconnect = options.onReconnect || null;
        this.onMaxReconnectAttemptsReached = options.onMaxReconnectAttemptsReached || null;
        this.onConnectionStatusChange = options.onConnectionStatusChange || null;
        this.lastPingReceived = Date.now();
        this.pingCheckInterval = null;
        this.reconnectTimeout = null;
        this.isDestroyed = false;
        this.lastCloseEvent = null;
        
        this.init();
    }
    
    init() {
        if (!this.websocket) {
            console.error('WebSocket instance is required');
            return;
        }
        
        // Store original connection details for reconnection
        this.originalUrl = this.websocket.url;
        // Only store protocols if they exist and are not empty
        this.originalProtocols = this.websocket.protocol && this.websocket.protocol.length > 0 
            ? this.websocket.protocol 
            : undefined;
        
        // Set up event listeners
        this.setupEventListeners();
        
        // Always start ping monitoring to detect stale connections even in managed mode
        this.startPingMonitoring();
    }
    
    startPingMonitoring() {
        // Clear existing interval
        if (this.pingCheckInterval) {
            clearInterval(this.pingCheckInterval);
        }
        
        // Track visibility to avoid false ping timeouts after background
        this._lastVisibleTime = Date.now();
        
        // Handle visibility changes to reset ping timer when tab becomes visible
        // This prevents false disconnections when tab was in background
        if (typeof document !== 'undefined' && !this._visibilityHandler) {
            this._visibilityHandler = () => {
                if (document.visibilityState === 'visible') {
                    // Give the connection a grace period after coming back from background
                    // Reset ping timer so we don't immediately disconnect
                    this.log('👁️ Tab became visible, resetting ping timer');
                    this.lastPingReceived = Date.now();
                    this._lastVisibleTime = Date.now();
                }
            };
            document.addEventListener('visibilitychange', this._visibilityHandler);
        }
        
        // Check for ping timeout every 10 seconds
        this.pingCheckInterval = setInterval(() => {
            if (this.isDestroyed) return;
            
            // Skip ping timeout check if tab is not visible (throttled timers can cause false positives)
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }
            
            // Also skip if we just became visible (grace period of 30 seconds)
            const timeSinceVisible = Date.now() - (this._lastVisibleTime || 0);
            if (timeSinceVisible < 30000) {
                return;
            }
            
            const timeSinceLastPing = Date.now() - this.lastPingReceived;
            
            if (timeSinceLastPing > this.options.pingTimeout && this.websocket.readyState === WebSocket.OPEN) {
                this.log('⚠️ Ping timeout detected, connection may be dead');
                // Force close the connection to trigger reconnection
                this.websocket.close(1000, 'Ping timeout');
            }
        }, 10000);
    }
    
    setupEventListeners() {
        const originalOnOpen = this.websocket.onopen;
        const originalOnMessage = this.websocket.onmessage;
        const originalOnClose = this.websocket.onclose;
        const originalOnError = this.websocket.onerror;
        
        // Only set onopen if WebSocket is not already open
        if (this.websocket.readyState !== WebSocket.OPEN) {
            this.websocket.onopen = (event) => {
                this.log('🟢 WebSocket connection opened (from keepalive)');
                this.reconnectAttempts = 0;
                this.lastPingReceived = Date.now(); // Reset ping timer
                
                // Notify status change
                if (this.onConnectionStatusChange) {
                    this.onConnectionStatusChange('connected');
                }
                
                if (originalOnOpen) originalOnOpen.call(this.websocket, event);
            };
        } else {
            // If already open, reset ping timer and notify
            this.lastPingReceived = Date.now();
            if (this.onConnectionStatusChange) {
                this.onConnectionStatusChange('connected');
            }
        }
        
        this.websocket.onmessage = (event) => {
            this.handleMessage(event);
            if (originalOnMessage) originalOnMessage.call(this.websocket, event);
        };
        
        this.websocket.onclose = (event) => {
            this.log('🔴 WebSocket connection closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
            
            // Store close event for backoff logic - but only if websocket still exists
            if (this.websocket) {
                this.websocket.closeEvent = event;
            }
            
            // Store close event on the instance for access in scheduleReconnect
            this.lastCloseEvent = event;
            
            // Notify status change
            if (this.onConnectionStatusChange) {
                this.onConnectionStatusChange('disconnected');
            }
            
            if (this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts && !this.isDestroyed) {
                this.scheduleReconnect();
            } else if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
                this.log('🚫 Max reconnection attempts reached');
                if (this.onMaxReconnectAttemptsReached) {
                    this.onMaxReconnectAttemptsReached();
                }
            }
            
            if (originalOnClose && this.websocket) {
                originalOnClose.call(this.websocket, event);
            }
        };
        
        this.websocket.onerror = (event) => {
            this.log('⚠️ WebSocket error occurred:', event);
            
            // Notify status change
            if (this.onConnectionStatusChange) {
                this.onConnectionStatusChange('error');
            }
            
            if (originalOnError) originalOnError.call(this.websocket, event);
        };
    }
    
    handleMessage(event) {
        // In managed mode, don't handle ping/pong - let WebSocketManager handle it
        if (this.options.managedMode) {
            return;
        }
        
        try {
            const data = JSON.parse(event.data);
            
            // Handle ping from server - respond with pong
            if (data.type === 'ping') {
                this.log('📡 Received PING from server:', data);
                this.lastPingReceived = Date.now(); // Update last ping time
                this.sendPong();
                return;
            }
            
            // Log if we receive a pong (shouldn't happen since we don't send pings)
            if (data.type === 'pong') {
                this.log('⚠️ Received unexpected PONG from server:', data);
                return;
            }
        } catch (e) {
            // Not JSON or doesn't have type field, ignore for keepalive purposes
        }
    }
    
    sendPong() {
        if (this.websocket.readyState !== WebSocket.OPEN) {
            this.log('❌ Cannot send pong - WebSocket not open');
            return;
        }
        
        try {
            const pongMessage = {
                type: 'pong',
                timestamp: new Date().toISOString()
            };
            
            this.websocket.send(JSON.stringify(pongMessage));
            
            this.log('🏓 PONG sent successfully:', pongMessage);
        } catch (error) {
            this.log('❌ Error sending pong:', error);
        }
    }
    
    scheduleReconnect() {
        if (this.isDestroyed) return;
        
        if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
            this.log('🚫 Max reconnection attempts reached');
            if (this.onMaxReconnectAttemptsReached) {
                this.onMaxReconnectAttemptsReached();
            }
            return;
        }
        
        this.reconnectAttempts++;
        
        let backoffDelay;
        if (this.lastCloseEvent && this.lastCloseEvent.code === 1000) {
            backoffDelay = this.options.reconnectInterval * Math.pow(3, this.reconnectAttempts - 1);
        } else if (this.lastCloseEvent && this.lastCloseEvent.code === 3001) {
            backoffDelay = this.options.reconnectInterval * Math.pow(2.5, this.reconnectAttempts - 1);
        } else {
            backoffDelay = this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);
        }
        
        // Cap the delay at 2 minutes, then add +/- 30% jitter to prevent thundering herd
        const capped = Math.min(backoffDelay, 120000);
        const jitter = capped * (0.7 + Math.random() * 0.6); // range: [0.7x, 1.3x]
        const finalDelay = Math.round(jitter);
        
        this.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.options.maxReconnectAttempts} in ${finalDelay}ms (close code: ${this.lastCloseEvent?.code || 'unknown'})`);
        
        // Notify status change
        if (this.onConnectionStatusChange) {
            this.onConnectionStatusChange('reconnecting');
        }
        
        // Clear any existing reconnect timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        
        this.reconnectTimeout = setTimeout(() => {
            if (!this.isDestroyed) {
                this.reconnect();
            }
        }, finalDelay);
    }
    
    reconnect() {
        if (this.isDestroyed) return;
        
        // Check network status before attempting reconnection (PWA optimization)
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.log('📴 Network offline, delaying reconnection attempt');
            // Schedule retry when network comes back or after longer delay
            this.scheduleReconnect();
            return;
        }
        
        this.log('🔌 Attempting to reconnect...');
        
        try {
            // Create new WebSocket connection
            // Only pass protocols if they exist and are valid
            const newWebSocket = this.originalProtocols 
                ? new WebSocket(this.originalUrl, this.originalProtocols)
                : new WebSocket(this.originalUrl);
            
            // Replace the old websocket reference
            this.websocket = newWebSocket;
            
            // Re-setup event listeners
            this.setupEventListeners();
            
            // Set up a timeout to check if connection succeeds
            const connectionTimeout = setTimeout(() => {
                if (newWebSocket.readyState !== WebSocket.OPEN) {
                    this.log('❌ Connection timeout, scheduling next attempt');
                    newWebSocket.close();
                    this.scheduleReconnect();
                }
            }, 10000); // 10 second timeout
            
            // Clear timeout if connection opens successfully
            newWebSocket.addEventListener('open', () => {
                clearTimeout(connectionTimeout);
                this.log('✅ Reconnection successful');
                
                // Notify parent component about reconnection
                if (this.onReconnect) {
                    this.onReconnect(newWebSocket);
                }
            }, { once: true });
            
            // Handle connection failure
            newWebSocket.addEventListener('error', () => {
                clearTimeout(connectionTimeout);
                this.log('❌ Reconnection failed');
                this.scheduleReconnect();
            }, { once: true });
            
        } catch (error) {
            this.log('❌ Reconnection failed:', error);
            this.scheduleReconnect();
        }
    }
    
    log(...args) {
        if (this.options.debug) {
            //console.log('[WebSocketKeepalive]', ...args);
        }
    }
    
    // Public methods
    destroy() {
        this.isDestroyed = true;
        
        // Clear intervals and timeouts
        if (this.pingCheckInterval) {
            clearInterval(this.pingCheckInterval);
            this.pingCheckInterval = null;
        }
        
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        
        // Clean up visibility handler
        if (this._visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
        }
        
        this.websocket = null;
        this.onReconnect = null;
        this.onMaxReconnectAttemptsReached = null;
        this.onConnectionStatusChange = null;
        this.lastCloseEvent = null;
    }
    
    // Update token inside originalUrl so reconnections use the latest token
    updateToken(newToken) {
        if (!this.originalUrl) return;
        try {
            const url = new URL(this.originalUrl);
            url.searchParams.set('token', newToken);
            this.originalUrl = url.toString();
        } catch (e) {
            // ignore
        }
    }
    
    getStats() {
        return {
            reconnectAttempts: this.reconnectAttempts,
            websocketState: this.websocket ? this.websocket.readyState : null,
            lastPingReceived: this.lastPingReceived,
            timeSinceLastPing: Date.now() - this.lastPingReceived,
            isDestroyed: this.isDestroyed
        };
    }
    
    // Update websocket reference (useful when parent component creates new connection)
    updateWebSocket(newWebSocket) {
        this.websocket = newWebSocket;
        this.originalUrl = newWebSocket.url;
        // Only store protocols if they exist and are not empty
        this.originalProtocols = newWebSocket.protocol && newWebSocket.protocol.length > 0 
            ? newWebSocket.protocol 
            : undefined;
        this.reconnectAttempts = 0; // Reset reconnection attempts
        this.lastPingReceived = Date.now(); // Reset ping timer
        this.setupEventListeners();
    }
    
    // Force reconnection (useful for manual refresh)
    forceReconnect() {
        this.log('🔄 Force reconnect requested');
        this.reconnectAttempts = 0; // Reset attempts for manual reconnect
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.close(1000, 'Manual reconnect');
        } else {
            this.reconnect();
        }
    }
}

export default WebSocketKeepalive;